import { useState } from "react";
import { BarChart3 } from "lucide-react";
import { fetchWeeklyInsight, type DaySummary } from "../lib/ai";
import { friendlyError } from "../lib/errors";
import { cacheGet, cacheSet, DAY_MS } from "../lib/cache";
import { localDateOf } from "../lib/storage";
import type { Goals } from "../lib/goals";
import type { LoggedMeal } from "../lib/types";

interface Props {
  meals: LoggedMeal[];
  goals: Goals;
}

export default function WeeklyInsight({ meals, goals }: Props) {
  const cacheKey = `insight.${currentWeekKey()}`;
  const [text, setText] = useState<string | null>(cacheGet<string>(cacheKey));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    setLoading(true);
    setError(null);
    try {
      const days = summarizeLast7Days(meals);
      const t = await fetchWeeklyInsight(days, goals);
      setText(t);
      cacheSet(cacheKey, t, 3 * DAY_MS);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-4 p-4 rounded-2xl bg-gradient-to-br from-emerald-950/40 to-slate-900 border border-slate-800 space-y-3">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-slate-200 inline-flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4 text-emerald-400" strokeWidth={2} />
          Weekly insight
        </span>
        {text && (
          <button
            onClick={generate}
            disabled={loading}
            className="text-xs text-emerald-400 underline disabled:opacity-50"
          >
            {loading ? "Updating…" : "Refresh"}
          </button>
        )}
      </div>

      {text ? (
        <div className="text-sm text-slate-200 leading-relaxed">{text}</div>
      ) : (
        <>
          <div className="text-xs text-slate-400">
            Get an AI-generated summary of how this week is going.
          </div>
          <button
            onClick={generate}
            disabled={loading || meals.length === 0}
            className="w-full py-2 rounded-lg bg-emerald-500 text-slate-950 text-sm font-medium disabled:opacity-50"
          >
            {loading ? "Generating…" : "Generate"}
          </button>
        </>
      )}

      {error && (
        <div className="text-xs text-rose-300">{error}</div>
      )}
    </div>
  );
}

function summarizeLast7Days(meals: LoggedMeal[]): DaySummary[] {
  const out: DaySummary[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const iso = isoDate(d);
    const dayMeals = meals.filter((m) => localDateOf(m.logged_at) === iso);
    const totals = dayMeals.reduce(
      (a, m) => ({
        calories: a.calories + m.estimate.totals.calories,
        protein_g: a.protein_g + m.estimate.totals.protein_g,
        carbs_g: a.carbs_g + m.estimate.totals.carbs_g,
        fat_g: a.fat_g + m.estimate.totals.fat_g
      }),
      { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }
    );
    out.push({ date: iso, ...totals, meal_count: dayMeals.length });
  }
  return out;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function currentWeekKey(): string {
  const d = new Date();
  const year = d.getFullYear();
  const oneJan = new Date(year, 0, 1);
  const week = Math.ceil(((d.getTime() - oneJan.getTime()) / (24 * 60 * 60 * 1000) + oneJan.getDay() + 1) / 7);
  return `${year}-w${week}`;
}
